import React, { useState, useEffect } from 'react';
import { ApiToken } from '../types';
import ApiService from '../services/api';

const ApiTokens: React.FC = () => {
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedToken, setExpandedToken] = useState<string | null>(null);
  const [tokenUsage, setTokenUsage] = useState<{[key: string]: any}>({});
  const [editingToken, setEditingToken] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    hourly_limit: string;
    daily_limit: string;
    is_unlimited: boolean;
  }>({
    hourly_limit: '',
    daily_limit: '',
    is_unlimited: false
  });
  
  // Form state
  const [tokenName, setTokenName] = useState('');
  const [hourlyLimit, setHourlyLimit] = useState('50');
  const [dailyLimit, setDailyLimit] = useState('200');
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getTokens();
      // Sort tokens by ID in ascending order
      const sortedTokens = data.sort((a, b) => a.id.localeCompare(b.id));
      setTokens(sortedTokens);
      setError(null);
    } catch (error) {
      console.error('Error fetching tokens:', error);
      setError('Failed to fetch API tokens');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async () => {
    if (!tokenName.trim()) {
      alert('Please enter a token name');
      return;
    }

    try {
      setCreating(true);
      const newToken = await ApiService.createToken({
        name: tokenName,
        hourly_limit: isUnlimited ? null : parseInt(hourlyLimit) || null,
        daily_limit: isUnlimited ? null : parseInt(dailyLimit) || null,
        is_unlimited: isUnlimited
      });
      
      // Add new token and maintain sorted order by ID
      const updatedTokens = [...tokens, newToken].sort((a, b) => a.id.localeCompare(b.id));
      setTokens(updatedTokens);
      setTokenName('');
      setHourlyLimit('50');
      setDailyLimit('200');
      setIsUnlimited(false);
      setShowCreateForm(false);
      alert('Token created successfully!');
    } catch (error) {
      console.error('Error creating token:', error);
      alert('Failed to create token');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteToken = async (tokenId: string) => {
    if (!window.confirm('Are you sure you want to delete this token? This action cannot be undone.')) {
      return;
    }

    try {
      await ApiService.deleteToken(tokenId);
      setTokens(tokens.filter(token => token.id !== tokenId));
    } catch (error) {
      console.error('Error deleting token:', error);
      alert('Failed to delete token');
    }
  };

  const handleToggleToken = async (tokenId: string, isActive: boolean) => {
    try {
      await ApiService.updateToken(tokenId, { is_active: !isActive });
      setTokens(tokens.map(token => 
        token.id === tokenId ? { ...token, is_active: !isActive } : token
      ));
    } catch (error) {
      console.error('Error updating token:', error);
      alert('Failed to update token');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Token copied to clipboard!');
  };

  const handleExpandToken = async (tokenId: string) => {
    if (expandedToken === tokenId) {
      setExpandedToken(null);
      return;
    }

    setExpandedToken(tokenId);
    
    // Fetch usage stats if not already loaded
    if (!tokenUsage[tokenId]) {
      try {
        const usage = await ApiService.getTokenUsage(tokenId);
        setTokenUsage(prev => ({ ...prev, [tokenId]: usage }));
      } catch (error) {
        console.error('Error fetching token usage:', error);
      }
    }
  };

  const handleEditToken = (token: ApiToken) => {
    setEditingToken(token.id);
    setEditForm({
      hourly_limit: token.hourly_limit?.toString() || '',
      daily_limit: token.daily_limit?.toString() || '',
      is_unlimited: token.is_unlimited || false
    });
  };

  const handleSaveEdit = async (tokenId: string) => {
    try {
      await ApiService.updateToken(tokenId, {
        hourly_limit: editForm.is_unlimited ? null : parseInt(editForm.hourly_limit) || null,
        daily_limit: editForm.is_unlimited ? null : parseInt(editForm.daily_limit) || null,
        is_unlimited: editForm.is_unlimited
      });
      
      // Update local state
      setTokens(tokens.map(token => 
        token.id === tokenId 
          ? { 
              ...token, 
              hourly_limit: editForm.is_unlimited ? null : parseInt(editForm.hourly_limit) || null,
              daily_limit: editForm.is_unlimited ? null : parseInt(editForm.daily_limit) || null,
              is_unlimited: editForm.is_unlimited
            }
          : token
      ));
      
      setEditingToken(null);
      alert('Token updated successfully!');
    } catch (error) {
      console.error('Error updating token:', error);
      alert('Failed to update token');
    }
  };

  const handleCancelEdit = () => {
    setEditingToken(null);
    setEditForm({
      hourly_limit: '',
      daily_limit: '',
      is_unlimited: false
    });
  };

  const formatRateLimit = (token: ApiToken) => {
    if (token.is_unlimited) {
      return 'Unlimited';
    }
    
    const parts = [];
    if (token.hourly_limit) {
      parts.push(`${token.hourly_limit}/hour`);
    }
    if (token.daily_limit) {
      parts.push(`${token.daily_limit}/day`);
    }
    
    return parts.length > 0 ? parts.join('\n') : 'No limits';
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e9ecef',
          borderTop: '4px solid #4263eb',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  if (error && tokens.length === 0) {
    return (
      <div style={{
        padding: '24px',
        backgroundColor: '#fff9db',
        border: '1px solid #ffec99',
        borderRadius: '8px',
        color: '#e67700',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600 }}>Error</h3>
        <p style={{ margin: 0, fontSize: '14px' }}>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            marginTop: '12px',
            padding: '8px 16px',
            backgroundColor: '#e67700',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        {/* Active Tokens */}
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ marginBottom: '16px', color: '#495057', fontSize: '14px', fontWeight: 500 }}>
            Active API Tokens
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-end', 
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ 
                fontSize: '28px', 
                fontWeight: 700, 
                color: '#212529', 
                lineHeight: 1.2,
                marginBottom: '8px'
              }}>
                {tokens.filter(t => t.is_active).length}
              </div>
              <div style={{ 
                color: '#40c057', 
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span style={{ marginRight: '4px' }}>✓</span>
                Ready to use
              </div>
            </div>
            <div style={{
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#e7f5ff',
              borderRadius: '8px',
              color: '#1971c2',
              fontSize: '20px'
            }}>
              🔑
            </div>
          </div>
        </div>

        {/* Total Tokens */}
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ marginBottom: '16px', color: '#495057', fontSize: '14px', fontWeight: 500 }}>
            Total Tokens
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-end', 
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ 
                fontSize: '28px', 
                fontWeight: 700, 
                color: '#212529', 
                lineHeight: 1.2,
                marginBottom: '8px'
              }}>
                {tokens.length}
              </div>
              <div style={{ 
                color: '#495057', 
                fontSize: '13px'
              }}>
                {tokens.filter(t => !t.is_active).length} inactive
              </div>
            </div>
            <div style={{
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fff5f5',
              borderRadius: '8px',
              color: '#c53030',
              fontSize: '20px'
            }}>
                              📋
            </div>
          </div>
        </div>

        {/* Rate Limited Tokens */}
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e9ecef'
        }}>
          <div style={{ marginBottom: '16px', color: '#495057', fontSize: '14px', fontWeight: 500 }}>
            Rate Limited
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-end', 
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ 
                fontSize: '28px', 
                fontWeight: 700, 
                color: '#212529', 
                lineHeight: 1.2,
                marginBottom: '8px'
              }}>
                {tokens.filter(t => !t.is_unlimited).length}
              </div>
              <div style={{ 
                color: '#f59e0b', 
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span style={{ marginRight: '4px' }}>⏱</span>
                {tokens.filter(t => t.is_unlimited).length} unlimited
              </div>
            </div>
            <div style={{
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fff7ed',
              borderRadius: '8px',
              color: '#ea580c',
              fontSize: '20px'
            }}>
              ⚡
            </div>
          </div>
        </div>
      </div>

      {/* Create Token Section */}
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e9ecef',
        marginBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: showCreateForm ? '24px' : '0'
        }}>
          <div>
          <h3 style={{
            fontSize: '18px',
              fontWeight: '600',
              color: '#212529',
              margin: 0,
              marginBottom: '4px'
          }}>
              Create New API Token
          </h3>
            <p style={{ color: '#495057', fontSize: '14px', margin: 0 }}>
              Generate a new token for API access with custom rate limits.
        </p>
          </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          style={{
              padding: '8px 16px',
              background: showCreateForm ? '#6c757d' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
              border: 'none',
              borderRadius: '6px',
            cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
              gap: '8px'
          }}
          >
            {showCreateForm ? '✕ Cancel' : '+ Create Token'}
        </button>
      </div>

      {showCreateForm && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #e9ecef'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#212529',
                marginBottom: '6px'
              }}>
                Token Name
              </label>
              <input
                type="text"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                placeholder="Enter token name"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#212529',
                marginBottom: '6px'
              }}>
                Hourly Limit
              </label>
              <input
                type="number"
                value={hourlyLimit}
                onChange={(e) => setHourlyLimit(e.target.value)}
                disabled={isUnlimited}
                placeholder="50"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  backgroundColor: isUnlimited ? '#f8f9fa' : 'white'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#212529',
                marginBottom: '6px'
              }}>
                Daily Limit
              </label>
              <input
                type="number"
                value={dailyLimit}
                onChange={(e) => setDailyLimit(e.target.value)}
                disabled={isUnlimited}
                placeholder="200"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  backgroundColor: isUnlimited ? '#f8f9fa' : 'white'
                }}
              />
          </div>

            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              fontSize: '14px',
              fontWeight: '500',
                color: '#212529',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={isUnlimited}
                onChange={(e) => setIsUnlimited(e.target.checked)}
                  className="form-checkbox"
              />
                Unlimited Usage
            </label>
          </div>

            <div style={{ display: 'flex', alignItems: 'end' }}>
            <button
              onClick={handleCreateToken}
              disabled={creating}
              style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: creating ? '#6c757d' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: creating ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
              }}
            >
                {creating ? '⏳ Creating...' : '+ Create Token'}
            </button>
          </div>
        </div>
      )}
      </div>

      {/* Tokens List */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e9ecef',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e9ecef',
          backgroundColor: '#f8f9fa'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#212529',
            margin: 0,
            marginBottom: '4px'
          }}>
            API Tokens ({tokens.length})
          </h3>
          <p style={{ color: '#495057', fontSize: '14px', margin: 0 }}>
            Manage your existing API tokens and monitor their usage.
          </p>
        </div>

        {tokens.length === 0 ? (
        <div style={{
            padding: '48px 24px',
            textAlign: 'center',
            color: '#6c757d'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔑</div>
            <p style={{ fontSize: '16px', margin: 0 }}>No API tokens found</p>
            <p style={{ fontSize: '14px', margin: '8px 0 0 0' }}>Create your first token to get started</p>
          </div>
        ) : (
          <div>
            {/* Desktop Table Header */}
            <div className="desktop-table" style={{
          display: 'grid',
              gridTemplateColumns: '2fr 2fr 1.5fr 1fr 1fr 1fr 1.5fr',
          padding: '16px 24px',
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #e9ecef',
              fontSize: '12px',
              fontWeight: '600',
              color: '#495057',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
        }}>
              <div>Token Name</div>
              <div>Token Key</div>
              <div>Rate Limits</div>
              <div>Usage</div>
              <div>Status</div>
              <div>Created</div>
              <div>Actions</div>
        </div>

            {/* Desktop Token Rows */}
            <div className="desktop-table">
              {tokens.map((token, index) => (
                <React.Fragment key={token.id}>
                  {editingToken === token.id ? (
                    // Edit Form Row
          <div style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 2fr 1.5fr 1fr 1fr 1fr 1.5fr',
                      padding: '16px 24px',
                      borderBottom: index < tokens.length - 1 ? '1px solid #e9ecef' : 'none',
                      alignItems: 'center',
                      backgroundColor: '#f8f9fa'
          }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#212529'
                    }}>
                      {token.name}
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <code style={{
                        fontSize: '12px',
                        backgroundColor: '#e9ecef',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        marginRight: '8px',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {token.token?.substring(0, 16)}...
                      </code>
                      <button
                        onClick={() => copyToClipboard(token.token || '')}
                        style={{
                          padding: '4px 8px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                        title="Copy token"
                      >
                        Copy
                      </button>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div>
                        <label style={{ fontSize: '12px', color: '#6c757d', display: 'block' }}>Hourly</label>
                        <input
                          type="number"
                          value={editForm.hourly_limit}
                          onChange={(e) => setEditForm(prev => ({ ...prev, hourly_limit: e.target.value }))}
                          disabled={editForm.is_unlimited}
                          style={{
                            width: '80px',
                            padding: '4px 6px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor: editForm.is_unlimited ? '#f8f9fa' : 'white'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', color: '#6c757d', display: 'block' }}>Daily</label>
                        <input
                          type="number"
                          value={editForm.daily_limit}
                          onChange={(e) => setEditForm(prev => ({ ...prev, daily_limit: e.target.value }))}
                          disabled={editForm.is_unlimited}
                          style={{
                            width: '80px',
                            padding: '4px 6px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor: editForm.is_unlimited ? '#f8f9fa' : 'white'
                          }}
                        />
                      </div>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '12px',
                        color: '#495057',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={editForm.is_unlimited}
                          onChange={(e) => setEditForm(prev => ({ ...prev, is_unlimited: e.target.checked }))}
                          className="form-checkbox"
                        />
                        Unlimited
                      </label>
                    </div>
                    
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      {tokenUsage[token.id] ? (
                        <div>
                          <div>Total: {tokenUsage[token.id].total_usage}</div>
                          <div>Today: {tokenUsage[token.id].daily_usage}</div>
          </div>
        ) : (
                        'Loading...'
                      )}
                    </div>
                    
                    <div>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: token.is_active ? '#10b981' : '#ef4444',
                        backgroundColor: token.is_active ? '#f0fdf4' : '#fef2f2',
                        padding: '4px 8px',
                        borderRadius: '6px'
                      }}>
                        {token.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      {token.created_at ? new Date(token.created_at).toLocaleDateString() : 'N/A'}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        onClick={() => handleSaveEdit(token.id)}
                style={{
                          padding: '4px 8px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                        title="Save changes"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                        title="Cancel editing"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                  ) : (
                    // Normal Token Row
                    <div style={{
                  display: 'grid',
                      gridTemplateColumns: '2fr 2fr 1.5fr 1fr 1fr 1fr 1.5fr',
                  padding: '16px 24px',
                      borderBottom: index < tokens.length - 1 ? '1px solid #e9ecef' : 'none',
                  alignItems: 'center'
                    }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                      color: '#212529'
                }}>
                  {token.name}
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <code style={{
                    fontSize: '12px',
                        backgroundColor: '#f8f9fa',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    marginRight: '8px',
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {token.token?.substring(0, 16)}...
                  </code>
                  <button
                    onClick={() => copyToClipboard(token.token || '')}
                    style={{
                          padding: '4px 8px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                      border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                    }}
                    title="Copy token"
                  >
                        Copy
                  </button>
                </div>
                
                <div style={{
                  fontSize: '12px',
                      color: '#6c757d',
                  whiteSpace: 'pre-line'
                }}>
                  {formatRateLimit(token)}
                </div>
                
                <div style={{
                  fontSize: '12px',
                      color: '#6c757d'
                }}>
                  {tokenUsage[token.id] ? (
                    <div>
                      <div>Total: {tokenUsage[token.id].total_usage}</div>
                      <div>Today: {tokenUsage[token.id].daily_usage}</div>
                      <button
                        onClick={() => handleExpandToken(token.id)}
                        style={{
                              padding: '2px 6px',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                          border: 'none',
                              borderRadius: '4px',
                              fontSize: '11px',
                          cursor: 'pointer',
                              marginTop: '4px'
                        }}
                        title={expandedToken === token.id ? 'Hide details' : 'View details'}
                      >
                            {expandedToken === token.id ? '▲' : '▼'}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleExpandToken(token.id)}
                      style={{
                            padding: '4px 8px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                        border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer'
                      }}
                      title="Load usage statistics"
                    >
                          Load Stats
                    </button>
                  )}
                </div>
                
                <div>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: token.is_active ? '#10b981' : '#ef4444',
                    backgroundColor: token.is_active ? '#f0fdf4' : '#fef2f2',
                    padding: '4px 8px',
                    borderRadius: '6px'
                  }}>
                    {token.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <div style={{
                  fontSize: '12px',
                      color: '#6c757d'
                }}>
                  {token.created_at ? new Date(token.created_at).toLocaleDateString() : 'N/A'}
                </div>
                
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => handleEditToken(token)}
                    style={{
                          padding: '4px 8px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                      border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                    }}
                    title="Edit rate limits"
                  >
                        Edit
                  </button>
                  <button
                    onClick={() => handleToggleToken(token.id, token.is_active)}
                    style={{
                          padding: '4px 8px',
                          backgroundColor: token.is_active ? '#f59e0b' : '#10b981',
                          color: 'white',
                      border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                    }}
                    title={token.is_active ? 'Deactivate' : 'Activate'}
                  >
                        {token.is_active ? 'Pause' : 'Play'}
                  </button>
                  <button
                    onClick={() => handleDeleteToken(token.id)}
                    style={{
                          padding: '4px 8px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                      border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                    }}
                    title="Delete token"
                  >
                        Delete
                  </button>
                </div>
              </div>
                )}
              
              {/* Expanded Token Details */}
              {expandedToken === token.id && tokenUsage[token.id] && (
                <div style={{
                  gridColumn: '1 / -1',
                  padding: '16px 24px',
                  backgroundColor: '#f8f9fa',
                    borderBottom: index < tokens.length - 1 ? '1px solid #e9ecef' : 'none'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      backgroundColor: 'white',
                      padding: '12px',
                      borderRadius: '6px',
                        border: '1px solid #e9ecef'
                    }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Total Requests</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#212529' }}>
                        {tokenUsage[token.id].total_usage}
                      </div>
                    </div>
                    <div style={{
                      backgroundColor: 'white',
                      padding: '12px',
                      borderRadius: '6px',
                        border: '1px solid #e9ecef'
                    }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Hourly Usage</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#212529' }}>
                        {tokenUsage[token.id].hourly_usage}
                        {tokenUsage[token.id].hourly_limit && ` / ${tokenUsage[token.id].hourly_limit}`}
                      </div>
                    </div>
                    <div style={{
                      backgroundColor: 'white',
                      padding: '12px',
                      borderRadius: '6px',
                        border: '1px solid #e9ecef'
                    }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Daily Usage</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#212529' }}>
                        {tokenUsage[token.id].daily_usage}
                        {tokenUsage[token.id].daily_limit && ` / ${tokenUsage[token.id].daily_limit}`}
                      </div>
                    </div>
                    <div style={{
                      backgroundColor: 'white',
                      padding: '12px',
                      borderRadius: '6px',
                        border: '1px solid #e9ecef'
                    }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Last Used</div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#212529' }}>
                        {tokenUsage[token.id].last_usage 
                          ? new Date(tokenUsage[token.id].last_usage).toLocaleString()
                          : 'Never'
                        }
                      </div>
                    </div>
                  </div>
                  
                  {/* Daily Breakdown */}
                  {tokenUsage[token.id].daily_breakdown && tokenUsage[token.id].daily_breakdown.length > 0 && (
                    <div style={{
                      backgroundColor: 'white',
                      padding: '12px',
                      borderRadius: '6px',
                        border: '1px solid #e9ecef'
                    }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#212529', marginBottom: '8px' }}>
                        Last 7 Days Usage
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {tokenUsage[token.id].daily_breakdown.map((day: any) => (
                          <div key={day.date} style={{
                              backgroundColor: '#f8f9fa',
                            padding: '6px 8px',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            <div style={{ fontWeight: '500' }}>{new Date(day.date).toLocaleDateString()}</div>
                              <div style={{ color: '#6c757d' }}>{day.count} requests</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              </React.Fragment>
            ))}
            </div>

            {/* Mobile Token Cards */}
            {tokens.map((token, index) => (
              <div key={`mobile-${token.id}`} className="mobile-table-card">
                <div className="mobile-table-header">
                  <div className="mobile-table-title">{token.name}</div>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: token.is_active ? '#10b981' : '#ef4444',
                    backgroundColor: token.is_active ? '#f0fdf4' : '#fef2f2',
                    padding: '4px 8px',
                    borderRadius: '6px'
                  }}>
                    {token.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <div className="mobile-table-content">
                  <div className="mobile-table-row">
                    <span className="mobile-table-label">Token Key</span>
                    <span className="mobile-table-value">
                      <code style={{
                        fontSize: '11px',
                        backgroundColor: '#f8f9fa',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontFamily: 'monospace'
                      }}>
                        {token.token?.substring(0, 12)}...
                      </code>
                    </span>
                  </div>
                  
                  <div className="mobile-table-row">
                    <span className="mobile-table-label">Rate Limits</span>
                    <span className="mobile-table-value">
                      {formatRateLimit(token)}
                    </span>
                  </div>
                  
                  <div className="mobile-table-row">
                    <span className="mobile-table-label">Usage</span>
                    <span className="mobile-table-value">
                      {tokenUsage[token.id] ? (
                        <div style={{ textAlign: 'right' }}>
                          <div>Total: {tokenUsage[token.id].total_usage}</div>
                          <div>Today: {tokenUsage[token.id].daily_usage}</div>
                        </div>
                      ) : (
                        'Tap to load'
                      )}
                    </span>
                  </div>
                  
                  <div className="mobile-table-row">
                    <span className="mobile-table-label">Created</span>
                    <span className="mobile-table-value">
                      {token.created_at ? new Date(token.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
                
                <div className="mobile-actions">
                  <button
                    onClick={() => copyToClipboard(token.token || '')}
                    className="btn btn-mobile"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white'
                    }}
                  >
                    📋 Copy
                  </button>
                  
                  {editingToken === token.id ? (
                    <>
                      <button
                        onClick={() => handleSaveEdit(token.id)}
                        className="btn btn-mobile"
                        style={{
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: 'white'
                        }}
                      >
                        ✓ Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="btn btn-mobile"
                        style={{
                          backgroundColor: '#6c757d',
                          color: 'white'
                        }}
                      >
                        ✕ Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleEditToken(token)}
                        className="btn btn-mobile"
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white'
                        }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleToggleToken(token.id, token.is_active)}
                        className="btn btn-mobile"
                        style={{
                          backgroundColor: token.is_active ? '#f59e0b' : '#10b981',
                          color: 'white'
                        }}
                      >
                        {token.is_active ? '⏸️ Pause' : '▶️ Play'}
                      </button>
                      <button
                        onClick={() => handleDeleteToken(token.id)}
                        className="btn btn-mobile"
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </>
                  )}
                </div>
                
                {/* Mobile Edit Form */}
              {editingToken === token.id && (
                <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef'
                }}>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '12px', color: '#6c757d', display: 'block', marginBottom: '4px' }}>
                        Hourly Limit
                      </label>
                      <input
                        type="number"
                        value={editForm.hourly_limit}
                        onChange={(e) => setEditForm(prev => ({ ...prev, hourly_limit: e.target.value }))}
                        disabled={editForm.is_unlimited}
                        className="form-input"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          fontSize: '14px',
                          backgroundColor: editForm.is_unlimited ? '#f8f9fa' : 'white'
                        }}
                      />
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '12px', color: '#6c757d', display: 'block', marginBottom: '4px' }}>
                        Daily Limit
                      </label>
                      <input
                        type="number"
                        value={editForm.daily_limit}
                        onChange={(e) => setEditForm(prev => ({ ...prev, daily_limit: e.target.value }))}
                        disabled={editForm.is_unlimited}
                        className="form-input"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          fontSize: '14px',
                          backgroundColor: editForm.is_unlimited ? '#f8f9fa' : 'white'
                        }}
                      />
                    </div>
                    
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '14px',
                      color: '#495057',
                      cursor: 'pointer'
                    }}>
                        <input
                          type="checkbox"
                          checked={editForm.is_unlimited}
                          onChange={(e) => setEditForm(prev => ({ ...prev, is_unlimited: e.target.checked }))}
                        className="form-checkbox"
                        />
                      Unlimited Rate Limits
                      </label>
                    </div>
                )}
                    </div>
            ))}
                </div>
        )}
      </div>
    </div>
  );
};

export default ApiTokens; 